import helpData from '../../content/help/ai-visibility-help.json';
import type { HelpCategory, HelpContent, HelpEntry, ResolvedHelpText } from './help-types';

const HELP_CONTENT = helpData as HelpContent;

const CATEGORY_ORDER: HelpCategory[] = [
  'metrics',
  'tables',
  'concepts',
  'sections',
  'filters',
  'actions',
];

function parseHelpKey(key: string): { category: HelpCategory; id: string } | null {
  const trimmed = key.trim();
  if (!trimmed) return null;

  const dot = trimmed.indexOf('.');
  if (dot > 0) {
    const category = trimmed.slice(0, dot) as HelpCategory;
    const id = trimmed.slice(dot + 1);
    if (CATEGORY_ORDER.includes(category) && id) {
      return { category, id };
    }
  }

  return null;
}

export function lookupHelpEntry(key: string): { entry: HelpEntry; category: HelpCategory; id: string } | null {
  const explicit = parseHelpKey(key);
  if (explicit) {
    const entry = HELP_CONTENT[explicit.category]?.[explicit.id];
    if (entry) return { entry, category: explicit.category, id: explicit.id };
    return null;
  }

  for (const category of CATEGORY_ORDER) {
    const entry = HELP_CONTENT[category]?.[key];
    if (entry) return { entry, category, id: key };
  }

  return null;
}

export function resolveHelpText(key: string): ResolvedHelpText {
  const match = lookupHelpEntry(key);
  if (!match) {
    return {
      key,
      category: null,
      found: false,
      title: key,
      description: '',
    };
  }

  return {
    key: match.id,
    category: match.category,
    found: true,
    ...match.entry,
  };
}

/** Short description for native tooltips and chart overlays. */
export function getHelpDescription(key: string): string {
  return resolveHelpText(key).description;
}

/**
 * HTML content for shared `@/app/common/components/HelpTooltip`.
 * Returns empty string when the key is missing so the icon stays hidden.
 */
export function getHelpHtml(key: string): string {
  const help = resolveHelpText(key);
  if (!help.found) return '';

  // Use physical text-align:right — Bootstrap text-end is left in RTL layouts.
  const parts = [
    `<div dir="rtl" style="text-align:right">`,
    `<p class="fs--2 fw-semibold mb-1" style="text-align:right">${escapeHtml(help.title)}</p>`,
    `<p class="fs--2 lh-base mb-0" style="text-align:right">${escapeHtml(help.description)}</p>`,
  ];

  if (help.details) {
    parts.push(
      `<p class="fs--2 lh-base mt-2 mb-0" style="text-align:right">${escapeHtml(help.details)}</p>`,
    );
  }
  if (help.example) {
    parts.push(
      `<p class="fs--2 lh-base mt-2 mb-0 rounded-2 bg-black bg-opacity-25 px-2 py-1" style="text-align:right">${escapeHtml(help.example)}</p>`,
    );
  }

  parts.push('</div>');
  return parts.join('');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export { HELP_CONTENT };
